<template>
    <Container class="w-full">
        <div class="mt-3 flex items-center gap-2">
            <h1 class="text-sm font-semibold text-luuqa">Luuqa</h1>
            <span v-if="overview" class="flex items-center gap-1.5 rounded-full bg-nomad-900 px-2 py-0.5 text-[11px] text-nomad-100" :title="statusNote">
                <span class="h-1.5 w-1.5 rounded-full" :class="overview.enabled ? 'bg-luuqa' : 'bg-nomad-400'"></span>
                {{ overview.enabled ? "On for everyone" : "Off, early access only" }}
            </span>
            <button type="button" class="ml-auto flex h-7 w-7 items-center justify-center rounded-md text-sm text-nomad-100 hover:bg-nomad-900 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-luuqa"
                aria-label="Refresh" @click="refresh(true)">
                <span class="icon-refresh-cw" :class="{ 'animate-spin': refreshing }" aria-hidden="true"></span>
            </button>
        </div>

        <nav class="mt-1 flex gap-4 overflow-x-auto border-b border-nomad-800 [scrollbar-width:none]" aria-label="Luuqa">
            <button v-for="option in tabs" :key="option.key" type="button"
                class="-mb-px flex shrink-0 items-center gap-1.5 border-b-2 py-2 text-[13px] font-medium focus:outline-none focus-visible:text-luuqa"
                :class="tab === option.key ? 'border-luuqa text-white' : 'border-transparent text-nomad-200 hover:text-white'"
                :aria-current="tab === option.key ? 'page' : null"
                @click="open(option.key)">
                {{ option.label }}
                <span v-if="option.count" class="rounded-full px-1.5 text-[11px] font-semibold leading-4"
                    :class="option.urgent ? 'bg-luuqa text-white' : 'bg-nomad-800 text-nomad-100'">{{ option.count.toLocaleString() }}</span>
            </button>
        </nav>

        <p v-if="error" class="mt-3 text-xs text-red-400">{{ error }}</p>

        <div v-if="loading" class="mt-3 space-y-1.5" aria-label="Loading">
            <div v-for="n in 6" :key="n" class="h-10 animate-pulse rounded-lg bg-nomad-900"></div>
        </div>

        <div v-else-if="tab === 'overview' && overview" class="mt-3 space-y-3">
            <p v-if="!overview.enabled || !overview.migrated" class="text-xs text-nomad-200">{{ statusNote }}</p>

            <div class="grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-nomad-800 sm:grid-cols-4 xl:grid-cols-8">
                <component :is="stat.tab ? 'button' : 'div'" v-for="stat in stats" :key="stat.label" :type="stat.tab ? 'button' : null"
                    class="bg-nomad-900 px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-luuqa"
                    :class="stat.tab ? 'hover:bg-nomad-850' : ''"
                    @click="stat.tab && open(stat.tab)">
                    <p class="truncate text-[11px] text-nomad-200">{{ stat.label }}</p>
                    <p class="text-lg font-semibold leading-6" :class="stat.urgent ? 'text-luuqa' : 'text-white'">{{ stat.value.toLocaleString() }}</p>
                </component>
            </div>

            <div class="grid grid-cols-1 items-start gap-3 md:grid-cols-3">
                <section v-for="group in breakdowns" :key="group.title" class="rounded-lg bg-nomad-900 p-3">
                    <h2 class="text-xs font-semibold text-white">{{ group.title }}</h2>
                    <p v-if="!group.rows.length" class="mt-2 text-xs text-nomad-200">Nothing yet.</p>
                    <div v-for="row in group.rows.slice(0, 6)" :key="row.label" class="mt-2">
                        <div class="flex justify-between gap-3 text-xs">
                            <span class="truncate capitalize text-nomad-100">{{ row.label }}</span>
                            <span class="font-medium text-white">{{ row.total.toLocaleString() }}</span>
                        </div>
                        <div class="mt-1 h-1 rounded-full bg-nomad-800">
                            <div class="h-1 rounded-full bg-luuqa" :style="{ width: `${(row.total / group.top) * 100}%` }"></div>
                        </div>
                    </div>
                    <p v-if="group.rows.length > 6" class="mt-2 text-[11px] text-nomad-200">and {{ group.rows.length - 6 }} more</p>
                </section>
            </div>

            <section v-if="overview.repeat_opt_outs?.length" class="rounded-lg bg-nomad-900 p-3">
                <h2 class="text-xs font-semibold text-white">Opting out repeatedly</h2>
                <p class="mt-0.5 text-[11px] text-nomad-200">Opting out clears their limits and history, so this can be someone getting around them. Nothing happens automatically.</p>
                <div v-for="person in overview.repeat_opt_outs" :key="person.email"
                    class="mt-2 flex flex-wrap items-center justify-between gap-x-3 text-xs">
                    <span class="min-w-0 break-all text-white">{{ person.email }}</span>
                    <span class="text-nomad-200">{{ person.opt_outs }} times, last {{ formatDate(person.last_opt_out) }}</span>
                </div>
            </section>
        </div>

        <div v-else-if="tab === 'waiting' || tab === 'reports' || tab === 'profiles'" class="mt-3">
            <div v-if="tab === 'profiles'" class="mb-2 flex flex-wrap items-center gap-2">
                <input v-model="search" type="search" placeholder="Search name, city or email" :class="[field, 'w-full min-w-0 sm:w-auto sm:flex-1']" @input="searchSoon" />
                <select v-model="status" aria-label="Status" :class="[field, 'capitalize pr-8']" @change="loadProfiles(1)">
                    <option value="">All statuses</option>
                    <option v-for="option in statuses" :key="option" :value="option">{{ option }}</option>
                    <option value="awaiting_photo">Waiting for photo</option>
                </select>
                <span class="text-xs text-nomad-200">{{ profiles.total.toLocaleString() }} {{ profiles.total === 1 ? 'profile' : 'profiles' }}</span>
            </div>
            <p v-else-if="capped" class="mb-2 text-xs text-nomad-200">The oldest {{ rows.length }} of {{ capped.toLocaleString() }}. More appear as you clear these.</p>

            <div class="grid grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <div class="min-w-0 overflow-hidden rounded-lg bg-nomad-900" :class="{ 'hidden lg:block': showDetail }">
                    <p v-if="!rows.length" class="p-6 text-center text-xs text-nomad-200">{{ emptyText }}</p>
                    <div class="divide-y divide-nomad-800">
                        <template v-if="tab === 'reports'">
                            <button v-for="report in rows" :key="report.id" type="button" :class="rowClass(report)" @click="select(report)">
                                <span class="w-28 shrink-0 truncate text-xs font-medium text-luuqa">{{ reasonLabel(report.reason) }}</span>
                                <span class="min-w-0 flex-1 truncate text-[13px] text-white">{{ report.reported_name }}</span>
                                <span v-if="report.earlier_reports" class="shrink-0 rounded-full bg-nomad-800 px-1.5 text-[11px] text-nomad-100">{{ report.earlier_reports }} earlier</span>
                                <span class="shrink-0 text-[11px] text-nomad-200">{{ timeAgo(report.created_at) }}</span>
                            </button>
                        </template>
                        <template v-else>
                            <button v-for="profile in rows" :key="profile.id" type="button" :class="rowClass(profile)" @click="select(profile)">
                                <Avatar :url="profile.photos[0]?.url" :name="profile.display_name" class="h-7 w-7" />
                                <span class="min-w-0 flex-1">
                                    <span class="block truncate text-[13px] font-medium text-white">{{ profile.display_name || "No name yet" }}<template v-if="profile.age">, {{ profile.age }}</template></span>
                                    <span class="block truncate text-[11px] text-nomad-200">{{ profile.email || [profile.city, profile.country].filter(Boolean).join(" · ") }}</span>
                                </span>
                                <span v-if="tab === 'profiles'" class="hidden w-40 shrink-0 truncate text-xs text-nomad-100 md:block">{{ [profile.city, profile.country].filter(Boolean).join(" · ") }}</span>
                                <span class="flex shrink-0 justify-end gap-1 md:w-24">
                                    <span v-if="profile.changes_pending" class="rounded-full bg-luuqa/15 px-1.5 text-[11px] text-luuqa">Edit</span>
                                    <span v-if="profile.held" class="rounded-full bg-red-500/15 px-1.5 text-[11px] text-red-400">Hidden</span>
                                    <span v-if="profile.verification?.requested" class="rounded-full bg-yellow-500/15 px-1.5 text-[11px] text-yellow-400">Photo</span>
                                </span>
                                <span v-if="tab === 'profiles'" class="w-20 shrink-0 text-right">
                                    <span class="rounded-full px-2 py-0.5 text-[11px] font-medium capitalize" :class="statusClass(profile.status)">{{ profile.status }}</span>
                                </span>
                                <span v-else class="shrink-0 text-[11px] text-nomad-200">{{ timeAgo(profile.submitted_at) }}</span>
                            </button>
                        </template>
                    </div>
                    <Pager v-if="tab === 'profiles'" :page="profiles.page" :last="profiles.lastPage" @go="loadProfiles" />
                </div>

                <aside v-if="selected" class="rounded-lg bg-nomad-900 p-3 lg:sticky lg:top-4" :class="{ 'hidden lg:block': !showDetail }">
                    <button type="button" class="mb-2 text-xs text-luuqa lg:hidden" @click="mobileDetail = false">Back to the list</button>

                    <template v-if="tab === 'reports'">
                        <p class="text-xs font-semibold text-luuqa">{{ reasonLabel(selected.reason) }}</p>
                        <p class="mt-0.5 text-xs text-nomad-200">From {{ selected.reporter || 'someone who has left' }} about {{ selected.reported_name }}<template v-if="selected.earlier_reports">, {{ selected.earlier_reports }} earlier</template></p>
                        <p v-if="selected.details" class="mt-2 text-[13px] leading-5 text-white">{{ selected.details }}</p>
                        <blockquote v-if="selected.message_body" class="mt-2 rounded-md bg-nomad-800 p-2 text-[13px] text-white">{{ selected.message_body }}</blockquote>
                        <div class="my-3 border-t border-nomad-800"></div>
                        <ProfileDetail v-if="selected.reported" :profile="selected.reported" @remove-photo="removePhoto" />
                        <p v-else class="text-xs text-nomad-200">This person has left Luuqa.</p>
                        <div class="mt-3 flex flex-wrap gap-1.5">
                            <button type="button" :class="secondary" :disabled="busy" @click="dismiss(selected)">Dismiss</button>
                            <button v-if="selected.reported && selected.reported.status !== 'suspended'" type="button" :class="secondary" :disabled="busy" @click="requestVerification(selected.reported)">{{ verificationLabel(selected.reported) }}</button>
                            <button v-if="selected.reported" type="button" :class="danger" :disabled="busy" @click="suspend(selected.reported)">Suspend</button>
                        </div>
                    </template>

                    <ProfileDetail v-else :profile="selected" @remove-photo="removePhoto">
                        <template v-if="tab === 'waiting'">
                            <div v-if="rejecting === selected.id">
                                <textarea v-model="note" rows="2" maxlength="300" placeholder="What should they change? They'll see this."
                                    class="w-full rounded-md border border-nomad-700 bg-nomad-800 text-[13px] text-white placeholder:text-nomad-400 focus:border-luuqa focus:ring-luuqa"></textarea>
                                <div class="mt-1.5 flex gap-1.5">
                                    <button type="button" :class="primary" :disabled="busy || !note.trim()" @click="reject(selected)">Send back</button>
                                    <button type="button" :class="secondary" @click="rejecting = null">Cancel</button>
                                </div>
                            </div>
                            <div v-else class="flex flex-wrap gap-1.5">
                                <button type="button" :class="primary" :disabled="busy || !canApprove(selected)" @click="approve(selected)">Approve</button>
                                <button type="button" :class="secondary" :disabled="busy" @click="startReject(selected)">Send back</button>
                                <button type="button" :class="secondary" :disabled="busy" @click="requestVerification(selected)">{{ verificationLabel(selected) }}</button>
                                <button type="button" :class="danger" :disabled="busy" @click="suspend(selected)">Suspend</button>
                            </div>
                        </template>
                        <div v-else class="flex flex-wrap gap-1.5">
                            <button v-if="selected.status === 'suspended' || selected.held" type="button" :class="primary" :disabled="busy" @click="restore(selected)">Restore</button>
                            <button v-if="selected.status !== 'suspended'" type="button" :class="secondary" :disabled="busy" @click="requestVerification(selected)">{{ verificationLabel(selected) }}</button>
                            <button v-if="selected.status !== 'suspended'" type="button" :class="secondary" :disabled="busy" @click="suspend(selected)">Suspend</button>
                            <button type="button" :class="danger" :disabled="busy" @click="removeProfile(selected)">Delete from Luuqa</button>
                        </div>
                    </ProfileDetail>
                </aside>
            </div>
        </div>

        <div v-else-if="tab === 'access'" class="mt-3">
            <p class="text-xs text-nomad-200">Let people in one at a time while Luuqa is off for everyone. Opting out gives this up.</p>
            <div class="mt-2 flex flex-wrap items-center gap-2">
                <input v-model="accessSearch" type="search" placeholder="Search opt-ins by name or email" :class="[field, 'min-w-0 flex-1']" @input="searchAccessSoon" />
                <form class="flex min-w-0 flex-1 gap-1.5" @submit.prevent="grantByEmail">
                    <input v-model="accessEmail" type="email" placeholder="Or add any account by email" :class="[field, 'min-w-0 flex-1']" />
                    <button type="submit" :class="[secondary, 'h-8']" :disabled="busy || !accessEmail.trim()">Let in</button>
                </form>
                <label class="flex items-center gap-1.5 text-xs text-nomad-100">
                    <input v-model="accessOnly" type="checkbox" class="rounded border-nomad-700 bg-nomad-800 text-luuqa focus:ring-luuqa" @change="loadAccess(1)" />
                    Only people with access
                </label>
                <span class="text-xs text-nomad-200">{{ access.total.toLocaleString() }} {{ access.total === 1 ? 'person' : 'people' }}</span>
            </div>
            <div class="mt-2 overflow-hidden rounded-lg bg-nomad-900">
                <p v-if="!access.rows.length" class="p-6 text-center text-xs text-nomad-200">Nobody found.</p>
                <div class="divide-y divide-nomad-800">
                    <div v-for="person in access.rows" :key="person.user_id" class="flex items-center gap-3 px-3 py-1.5">
                        <div class="min-w-0 flex-1">
                            <p class="truncate text-[13px] font-medium text-white">{{ person.email }}</p>
                            <p class="truncate text-[11px] capitalize text-nomad-200">{{ [person.name, person.gender, person.country].filter(Boolean).join(" · ") }}</p>
                        </div>
                        <button v-if="person.has_access" type="button" :class="danger" :disabled="busy" @click="revokeAccess(person)">Remove access</button>
                        <button v-else type="button" :class="primary" :disabled="busy" @click="grantAccess(person)">Let in</button>
                    </div>
                </div>
                <Pager :page="access.page" :last="access.lastPage" @go="loadAccess" />
            </div>
        </div>

        <div v-else-if="tab === 'optOuts'" class="mt-3">
            <p class="text-xs text-nomad-200">Opting out deletes their Luuqa data, so this record is what's left of it. It goes when they delete their account.</p>
            <div class="mt-2 flex items-center gap-2">
                <input v-model="optOutSearch" type="search" placeholder="Search by name or email" :class="[field, 'min-w-0 flex-1']" @input="searchOptOutsSoon" />
                <span class="text-xs text-nomad-200">{{ optOuts.total.toLocaleString() }} {{ optOuts.total === 1 ? 'opt-out' : 'opt-outs' }}</span>
            </div>
            <div class="mt-2 overflow-hidden rounded-lg bg-nomad-900">
                <p v-if="!optOuts.rows.length" class="p-6 text-center text-xs text-nomad-200">No opt-outs found.</p>
                <div class="divide-y divide-nomad-800">
                    <div v-for="event in optOuts.rows" :key="event.id" class="flex items-center gap-3 px-3 py-1.5">
                        <div class="min-w-0 flex-1">
                            <p class="truncate text-[13px] font-medium text-white">{{ event.email }}</p>
                            <p class="truncate text-[11px] text-nomad-200">{{ [event.name, formatDate(event.at), event.times === 1 ? 'Once' : `${event.times} times in all`].filter(Boolean).join(' · ') }}</p>
                        </div>
                        <span class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium" :class="event.in_luuqa_now ? 'bg-luuqa/15 text-luuqa' : 'bg-nomad-800 text-nomad-100'">{{ event.in_luuqa_now ? 'Back in Luuqa' : event.let_in ? 'Let in, not back yet' : 'Still out' }}</span>
                    </div>
                </div>
                <Pager :page="optOuts.page" :last="optOuts.lastPage" @go="loadOptOuts" />
            </div>
        </div>

        <div v-else-if="tab === 'countries'" class="mt-3">
            <div class="flex flex-wrap items-center gap-2">
                <input v-model="countrySearch" type="search" placeholder="Search countries" :class="[field, 'min-w-0 flex-1']" />
                <button type="button" :class="[secondary, 'h-8']" :disabled="busy" @click="setAll(true)">Open all</button>
                <button type="button" :class="[danger, 'h-8']" :disabled="busy" @click="setAll(false)">Close all</button>
            </div>
            <p class="mt-2 text-xs text-nomad-200">{{ openCount }} of {{ countries.length }} open. People in a closed country can't join, and don't appear in Discover.</p>
            <div class="mt-2 grid grid-cols-1 gap-px overflow-hidden rounded-lg bg-nomad-800 sm:grid-cols-2 lg:grid-cols-4">
                <button v-for="country in shownCountries" :key="country.code" type="button" role="switch"
                    :aria-checked="country.open" :disabled="busy"
                    class="flex h-9 items-center justify-between gap-3 bg-nomad-900 px-3 text-left hover:bg-nomad-850 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-luuqa disabled:opacity-60"
                    @click="setCountry(country)">
                    <span class="min-w-0 truncate text-xs text-white">{{ country.name }}</span>
                    <span class="flex h-4 w-7 shrink-0 items-center rounded-full p-0.5 transition-colors" :class="country.open ? 'justify-end bg-luuqa' : 'justify-start bg-nomad-700'">
                        <span class="h-3 w-3 rounded-full bg-white"></span>
                    </span>
                </button>
            </div>
        </div>
    </Container>
</template>
<script>
import axios from "axios";
import Container from "@/Pages/Backstage/Container.vue";
import Avatar from "@/components/luuqa/Avatar.vue";
import Pager from "@/components/luuqa/Pager.vue";
import ProfileDetail, { statusClass } from "@/components/luuqa/ProfileDetail.vue";

const API = "/api/backstage/luuqa";

const REASONS = {
    fake_profile: "Fake profile",
    inappropriate: "Inappropriate",
    harassment: "Harassment",
    money: "Asking for money",
    underage: "May be under 18",
    other: "Other",
};

export default {
    components: { Container, Avatar, Pager, ProfileDetail },
    data() {
        return {
            tab: "overview",
            overview: null,
            queue: { profiles: [], reports: [] },
            profiles: { rows: [], page: 1, lastPage: 1, total: 0 },
            countries: [],
            access: { rows: [], page: 1, lastPage: 1, total: 0 },
            optOuts: { rows: [], page: 1, lastPage: 1, total: 0 },
            optOutSearch: "",
            optOutTimer: null,
            accessSearch: "",
            accessOnly: false,
            accessEmail: "",
            accessTimer: null,
            search: "",
            status: "",
            countrySearch: "",
            statuses: ["pending", "approved", "rejected", "suspended", "draft", "hidden"],
            searchTimer: null,
            loading: true,
            refreshing: false,
            selectedId: null,
            mobileDetail: false,
            busy: false,
            error: null,
            rejecting: null,
            note: "",
            field: "h-8 rounded-md border border-nomad-800 bg-nomad-900 py-0 text-xs text-white placeholder:text-nomad-400 focus:border-luuqa focus:ring-luuqa",
            primary: "h-7 rounded-md bg-luuqa px-2.5 text-xs font-semibold text-white hover:bg-luuqa/80 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white",
            secondary: "h-7 rounded-md bg-nomad-800 px-2.5 text-xs font-medium text-white hover:bg-nomad-700 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-luuqa",
            danger: "h-7 rounded-md bg-nomad-800 px-2.5 text-xs font-medium text-red-400 hover:bg-nomad-700 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-luuqa",
        };
    },
    computed: {
        tabs() {
            const overview = { key: "overview", label: "Overview" };
            // Everything else needs the Luuqa tables
            const optOuts = this.overview?.opt_outs === null || this.overview?.opt_outs === undefined
                ? []
                : [{ key: "optOuts", label: "Opt-outs", count: this.overview.opt_outs }];
            if (!this.overview?.migrated) return [overview, ...optOuts];
            return [
                overview,
                { key: "waiting", label: "Waiting", count: this.overview.luuqa.waiting, urgent: true },
                { key: "reports", label: "Reports", count: this.overview.luuqa.open_reports, urgent: true },
                { key: "profiles", label: "All profiles" },
                ...optOuts,
                { key: "countries", label: "Countries" },
                ...(this.overview.early_access === null ? [] : [{ key: "access", label: "Early access", count: this.overview.early_access }]),
            ];
        },
        statusNote() {
            const { migrated, enabled, early_access: invited } = this.overview;
            if (!migrated) return "The Luuqa migrations haven't run on this server yet, so only opt-ins are available.";
            if (enabled) return "People can use Luuqa in the app.";
            const others = invited ? ` and the ${invited} ${invited === 1 ? "person" : "people"} you let in` : "";
            return `You can set it up here. To open it, set LUUQA_ENABLED=true on the server. Admins${others} can already use it.`;
        },
        stats() {
            const optIns = { label: "Opt-ins", value: this.overview.opt_ins.total };
            const luuqa = this.overview.luuqa;
            if (!luuqa) return [optIns];
            return [
                { label: "Waiting for review", value: luuqa.waiting, tab: "waiting", urgent: luuqa.waiting > 0 },
                { label: "Open reports", value: luuqa.open_reports, tab: "reports", urgent: luuqa.open_reports > 0 },
                optIns,
                { label: "Approved", value: luuqa.by_status.approved ?? 0 },
                { label: "Waiting for photo", value: luuqa.awaiting_photo ?? 0 },
                { label: "Suspended", value: luuqa.by_status.suspended ?? 0 },
                { label: "Matches", value: luuqa.matches },
                { label: "Messages", value: luuqa.messages },
            ];
        },
        breakdowns() {
            const optIns = this.overview.opt_ins;
            const statuses = Object.entries(this.overview.luuqa?.by_status ?? {})
                .map(([label, total]) => ({ label, total }))
                .sort((a, b) => b.total - a.total);
            return [
                { title: "Profiles by status", rows: statuses },
                { title: "Opt-ins by gender", rows: optIns.by_gender },
                { title: "Opt-ins by country", rows: optIns.by_country },
            ].map((group) => ({ ...group, top: Math.max(1, ...group.rows.map((row) => row.total)) }));
        },
        rows() {
            if (this.tab === "waiting") return this.queue.profiles;
            if (this.tab === "reports") return this.queue.reports;
            return this.tab === "profiles" ? this.profiles.rows : [];
        },
        /** Someone cleared from a queue is replaced by the next one, so reviewing keeps moving. */
        selected() {
            return this.rows.find((row) => row.id === this.selectedId) ?? this.rows[0] ?? null;
        },
        showDetail() {
            return this.mobileDetail && !!this.selected;
        },
        /** The queue only sends the oldest ones, so the total comes from the overview. */
        capped() {
            const luuqa = this.overview?.luuqa;
            const total = this.tab === "waiting" ? luuqa?.waiting : this.tab === "reports" ? luuqa?.open_reports : 0;
            return total > this.rows.length ? total : 0;
        },
        emptyText() {
            return { waiting: "Nothing waiting for review.", reports: "No open reports.", profiles: "No profiles found." }[this.tab];
        },
        openCount() {
            return this.countries.filter((country) => country.open).length;
        },
        shownCountries() {
            const query = this.countrySearch.trim().toLowerCase();
            return this.countries.filter((country) => country.name.toLowerCase().includes(query));
        },
    },
    mounted() {
        this.refresh();
    },
    methods: {
        statusClass,
        rowClass(row) {
            return [
                "flex w-full items-center gap-2.5 px-3 py-1.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-luuqa",
                this.selected?.id === row.id ? "lg:bg-luuqa/10" : "hover:bg-nomad-850",
            ];
        },
        select(row) {
            this.selectedId = row.id;
            this.mobileDetail = true;
            this.rejecting = null;
        },
        timeAgo(at) {
            if (!at) return "";
            const minutes = Math.max(0, Math.floor((Date.now() - new Date(at)) / 60000));
            if (minutes < 60) return `${minutes}m`;
            if (minutes < 1440) return `${Math.floor(minutes / 60)}h`;
            return `${Math.floor(minutes / 1440)}d`;
        },
        reasonLabel(reason) {
            return REASONS[reason] ?? reason;
        },
        open(tab) {
            this.tab = tab;
            this.selectedId = null;
            this.mobileDetail = false;
            this.rejecting = null;
            this.refresh();
        },
        /** The counts on the tabs come from the overview, so it's always reloaded too. */
        async refresh(quiet = false) {
            this.loading = !quiet;
            this.refreshing = true;
            this.error = null;
            try {
                this.overview = (await axios.get(`${API}/overview`)).data;
                if (this.tab === "waiting" || this.tab === "reports") {
                    this.queue = (await axios.get(`${API}/queue`)).data;
                } else if (this.tab === "profiles") {
                    await this.fetchProfiles(this.profiles.page);
                } else if (this.tab === "access") {
                    await this.fetchAccess(this.access.page);
                } else if (this.tab === "optOuts") {
                    await this.fetchOptOuts(this.optOuts.page);
                } else if (this.tab === "countries") {
                    this.countries = (await axios.get(`${API}/countries`)).data.countries;
                }
            } catch (error) {
                this.error = "Could not load Luuqa.";
            }
            this.loading = false;
            this.refreshing = false;
        },
        async fetchProfiles(page) {
            const params = { page };
            if (this.search.trim()) params.q = this.search.trim();
            if (this.status) params.status = this.status;
            const { data } = await axios.get(`${API}/profiles`, { params });
            this.profiles = { rows: data.profiles, page: data.page, lastPage: data.last_page, total: data.total };
        },
        async loadProfiles(page) {
            this.error = null;
            try {
                await this.fetchProfiles(page);
            } catch (error) {
                this.error = "Could not load profiles.";
            }
        },
        formatDate(at) {
            return new Date(at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
        },
        async fetchOptOuts(page) {
            const params = { page };
            if (this.optOutSearch.trim()) params.q = this.optOutSearch.trim();
            const { data } = await axios.get(`${API}/opt-outs`, { params });
            this.optOuts = { rows: data.opt_outs, page: data.page, lastPage: data.last_page, total: data.total };
        },
        async loadOptOuts(page) {
            this.error = null;
            try {
                await this.fetchOptOuts(page);
            } catch (error) {
                this.error = "Could not load opt-outs.";
            }
        },
        searchOptOutsSoon() {
            clearTimeout(this.optOutTimer);
            this.optOutTimer = setTimeout(() => this.loadOptOuts(1), 300);
        },
        async fetchAccess(page) {
            const params = { page };
            if (this.accessSearch.trim()) params.q = this.accessSearch.trim();
            if (this.accessOnly) params.only_with_access = 1;
            const { data } = await axios.get(`${API}/early-access`, { params });
            this.access = { rows: data.people, page: data.page, lastPage: data.last_page, total: data.total };
        },
        async loadAccess(page) {
            this.error = null;
            try {
                await this.fetchAccess(page);
            } catch (error) {
                this.error = "Could not load the list.";
            }
        },
        searchAccessSoon() {
            clearTimeout(this.accessTimer);
            this.accessTimer = setTimeout(() => this.loadAccess(1), 300);
        },
        grantAccess(person) {
            this.act(() => axios.post(`${API}/early-access/${person.user_id}`));
        },
        revokeAccess(person) {
            if (!window.confirm(`Remove early access for ${person.email}? They'll lose the way into Luuqa, and their profile stays.`)) return;
            this.act(() => axios.delete(`${API}/early-access/${person.user_id}`));
        },
        grantByEmail() {
            this.act(async () => {
                await axios.post(`${API}/early-access`, { email: this.accessEmail.trim() });
                this.accessEmail = "";
                this.accessOnly = true;
            });
        },
        searchSoon() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.loadProfiles(1), 300);
        },
        async act(request) {
            this.busy = true;
            this.error = null;
            try {
                await request();
                await this.refresh(true);
            } catch (error) {
                this.error = error.response?.data?.errors?.email?.[0]
                    ?? error.response?.data?.message
                    ?? "That didn't go through.";
            }
            this.busy = false;
        },
        canApprove(profile) {
            return !profile.gender_change || (!profile.verification?.requested && !!profile.verification?.url);
        },
        verificationLabel(profile) {
            return profile.verification?.requested ? "Ask again" : "Request photo verification";
        },
        requestVerification(profile) {
            if (!window.confirm(`Ask ${profile.display_name || "them"} for a verification photo? Their profile goes to pending, so they're hidden from others and can't use Discover or chats until you approve it.`)) return;
            this.act(() => axios.post(`${API}/profiles/${profile.id}/request-verification`));
        },
        approve(profile) {
            this.act(() => axios.post(`${API}/profiles/${profile.id}/approve`));
        },
        startReject(profile) {
            this.rejecting = profile.id;
            this.note = "";
        },
        reject(profile) {
            this.act(async () => {
                await axios.post(`${API}/profiles/${profile.id}/reject`, { note: this.note.trim() });
                this.rejecting = null;
            });
        },
        suspend(profile) {
            if (!window.confirm(`Suspend ${profile.display_name}? They'll be hidden from everyone.`)) return;
            this.act(() => axios.post(`${API}/profiles/${profile.id}/suspend`));
        },
        restore(profile) {
            if (!window.confirm(`Restore ${profile.display_name}? Open reports about them will be dismissed.`)) return;
            this.act(() => axios.post(`${API}/profiles/${profile.id}/restore`));
        },
        removeProfile(profile) {
            if (!window.confirm(`Delete ${profile.display_name} from Luuqa for good? Their profile, photos, matches and messages go. Their Heesify account stays.`)) return;
            this.act(() => axios.delete(`${API}/profiles/${profile.id}`));
        },
        removePhoto(photo) {
            if (!window.confirm("Remove this photo for good?")) return;
            this.act(() => axios.delete(`${API}/photos/${photo.id}`));
        },
        dismiss(report) {
            this.act(() => axios.post(`${API}/reports/${report.id}/dismiss`));
        },
        setCountry(country) {
            this.act(() => axios.put(`${API}/countries/${country.code}`, { open: !country.open }));
        },
        setAll(open) {
            if (!window.confirm(open ? "Open every country?" : "Close every country? Nobody will be able to join, and Discover will be empty.")) return;
            this.act(() => axios.put(`${API}/countries`, { open }));
        },
    },
};
</script>
